import { useState, useEffect, useMemo } from 'react';
import { api } from '~/trpc-extra/api';
import type { RouterOutputs } from '~/trpc-extra/api';
import { useRouter } from 'next/router';
import { Button } from '@mantine/core';
import { IconEye, IconPlayerPlayFilled, IconDeviceFloppy, IconExclamationCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import StablyLoader from '../../loader/StablyLoader';
import { MultiSelectMaxDisplayedItems } from './MultiSelectGroups';
import CopyButtonBadge from '~/components/common/CopyButtonBadge';
import CreatedBy from '~/components/test/CreatedBy';
import { useDebouncedValue } from '@mantine/hooks';
import DetailsFields from './DetailFields';
import { useCreateDraftAndPreview } from '~/hooks/test/useCreateDraftAndPreview';
import { match } from 'ts-pattern';

/* oxlint-disable @next/next/no-img-element */

export function DetailsPanel({
  test,
  testDraft,
  users,
  testSuites,
  projectId,
  closeModal,
}: {
  test?: RouterOutputs['test']['listTests'][number];
  testDraft?: RouterOutputs['testDraft']['list'][number];
  users?: RouterOutputs['team']['getUsersInfo'];
  testSuites?: RouterOutputs['testSuite']['listWithLatestRun'];
  projectId: string;
  closeModal: () => void;
}) {
  const router = useRouter();
  const trpcUtils = api.useUtils();
  const [suites, setSuites] = useState<{ id: string; name: string }[]>([]);
  const createDraftAndPreview = useCreateDraftAndPreview({ testContentId: test?.trunkContentId || '' });

  // Update local suites when test changes
  useEffect(() => {
    if (test?.testSuites) {
      setSuites(test.testSuites.map(({ id, name }) => ({ id, name })));
    }
  }, [test]);

  // Prevents rapid thumbnail queries when users rapidly switch between tests
  const currentId = useMemo(() => test?.id || testDraft?.id || '', [test, testDraft]);
  const [debouncedCurrentId] = useDebouncedValue(currentId, 150);

  const { data: testThumbnail } = api.test.getTestThumbnail.useQuery(
    { testId: test?.id || '' },
    {
      enabled: debouncedCurrentId === test?.id,
      staleTime: Infinity,
    },
  );
  const { data: testDraftThumbnail } = api.test.getTestThumbnail.useQuery(
    { testId: testDraft?.test?.id || '' },
    {
      enabled: debouncedCurrentId === testDraft?.id,
      staleTime: Infinity,
    },
  );
  const thumbnail = test ? testThumbnail : testDraftThumbnail;

  const { mutate: updateTestSuites, isPending: isAddingSuite } = api.testSuite.updateTests.useMutation({
    onMutate: () => {
      const id = notifications.show({
        color: 'gray',
        message: `Adding test to suite...`,
        icon: <IconDeviceFloppy />,
      });
      return { id };
    },
    onSuccess: (data, variables, context) => {
      notifications.update({
        id: context?.id,
        color: 'teal',
        message: (() => {
          const suiteName = testSuites?.find((suite) => suite.id === variables.testSuiteId)?.name ?? 'unknown suite';

          return match(variables.operation)
            .with('add', () => `Added test to ${suiteName}`)
            .with('remove', () => `Removed test from ${suiteName}`)
            .with('skip', () => `Test will be skipped in ${suiteName}`)
            .with('unskip', () => `Test will no longer be skipped in ${suiteName}`)
            .exhaustive();
        })(),
        icon: <IconDeviceFloppy />,
      });
      trpcUtils.test.listTests.invalidate().catch(() => undefined);
    },
    onError: (e) => {
      // Revert suites on error
      if (test?.testSuites) {
        setSuites(test.testSuites.map(({ id, name }) => ({ id, name })));
      }
      console.error(e);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const navigateToTest = async (options: { preview?: boolean } = {}) => {
    closeModal();
    if (test) {
      await (options.preview ? createDraftAndPreview() : router.push(`/project/${projectId}/test/${test.id}`));
    } else if (testDraft) {
      await router.push({
        pathname: `/project/${projectId}/testDraft/${testDraft.id}`,
        query: options.preview ? { preview: true } : undefined,
      });
    }
  };

  return (
    <div className="flex w-1/3 flex-col gap-4 border-l border-[#dee2e6] p-4 dark:border-[#424242]">
      <div>
        <div className="text-md font-semibold">{test?.name || testDraft?.test?.name}</div>
        <CopyButtonBadge value={test?.id || testDraft?.test?.id || ''} />
      </div>

      <div className="flex flex-col gap-2">
        <div
          className="relative aspect-video w-full cursor-pointer rounded-md bg-gray-200 dark:bg-[#424242]"
          onClick={() => navigateToTest()}
        >
          {thumbnail === undefined ?
            <div className="flex h-full w-full items-center justify-center">
              <StablyLoader
                strokeWidth={10}
                size={100}
                colorOverride="gray"
              />
            </div>
          : thumbnail ?
            <img
              src={thumbnail}
              alt="Test Thumbnail"
              className="absolute inset-0 h-full w-full rounded-md object-cover object-top"
            />
          : <div className="flex h-full w-full items-center justify-center">
              <img
                src="/assets/stably-full-logo.svg"
                alt="Stably Logo"
                className="h-1/2 w-1/2"
              />
            </div>
          }
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="w-full"
            variant="light"
            color="blue"
            size="xs"
            leftSection={<IconEye size={10} />}
            onClick={() => navigateToTest()}
          >
            View
          </Button>
          <Button
            className="w-full"
            variant="light"
            color="green"
            size="xs"
            leftSection={<IconPlayerPlayFilled size={10} />}
            onClick={() => navigateToTest({ preview: true })}
          >
            Preview
          </Button>
        </div>
      </div>

      <DetailsFields.List>
        <DetailsFields.Item label="Created">
          {test?.createdAt.toLocaleDateString() || testDraft?.createdAt.toLocaleDateString()}
        </DetailsFields.Item>
        <DetailsFields.Item label="Updated">
          {test?.updatedAt.toLocaleDateString() || testDraft?.updatedAt.toLocaleDateString()}
        </DetailsFields.Item>
        <DetailsFields.Item label="Created By">
          <CreatedBy email={users?.[(test || testDraft)?.createdByUserId ?? '']?.email || ''} />
        </DetailsFields.Item>
      </DetailsFields.List>

      {test ?
        <div className="mt-auto flex flex-col gap-2">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">Run Suites</div>
          <MultiSelectMaxDisplayedItems
            testSuites={testSuites}
            value={suites}
            disabled={isAddingSuite}
            onChange={(newSuites) => {
              if (newSuites.length > suites.length) {
                const addedSuite = newSuites.find((suite) => !suites.some((x) => x.id === suite.id));
                if (addedSuite && test) {
                  updateTestSuites({
                    operation: 'add',
                    projectId,
                    testSuiteId: addedSuite.id,
                    testIds: [test.id],
                  });
                }
              } else {
                const removedSuite = suites.find((suite) => !newSuites.some((x) => x.id === suite.id));
                if (removedSuite && test) {
                  updateTestSuites({
                    operation: 'remove',
                    projectId,
                    testSuiteId: removedSuite.id,
                    testIds: [test.id],
                  });
                }
              }
              setSuites(newSuites);
            }}
          />
        </div>
      : null}
    </div>
  );
}
